'use client'

import React, { useEffect, useState } from 'react'

import AProductTemplateCalendarDesktop from './a-product-template-calendar-desktop'
import AProductTemplateCalendarMobile from './a-product-template-calendar-mobile'

import type { CalendarTimeslot, DateSlot } from './a-product-template-calendar-types'

/** Slots shorter than this (minutes) use the compact day-list layout instead of the week timeline. */
const SHORT_SLOT_MAX_MINUTES = 10

function areAllSlotsShorterThan(slots: DateSlot[], minutes: number): boolean {
	return slots.length > 0 && slots.every(slot => slot.duration < minutes)
}

/**
 * Props for the presentational calendar grid (no data fetching or expand/collapse logic).
 *
 * @property slots - Flat list of {@link DateSlot} items for the active week range.
 * @property isSlotsLoading - When true, child views should show loading instead of an empty grid.
 * @property onSlotSelect - Fired when the user confirms a slot in either layout.
 * @property startHour - First timeline hour (inclusive) on desktop; drives hour labels.
 * @property endHour - Hour after the last row (exclusive).
 * @property dateFrom - Optional `yyyy-MM-dd` week start hint for {@link useCalendarCore}.
 * @property dateTo - Optional `yyyy-MM-dd` week end hint (passed through for symmetry).
 * @property onWeekChange - Notifies parent to load slots for another `{ dateFrom, dateTo }` window.
 */
export interface AProductTemplateCalendarViewProps {
	slots?: DateSlot[]
	isSlotsLoading?: boolean
	onSlotSelect?: (selectedSlot: CalendarTimeslot) => void
	startHour?: number
	endHour?: number
	dateFrom?: string
	dateTo?: string
	onWeekChange?: (dateFrom: string, dateTo: string) => void
}

/**
 * Responsive switch between mobile (day pills + slot list) and desktop (week grid) calendars.
 *
 * @param props - See {@link AProductTemplateCalendarViewProps}.
 *
 * @remarks Uses the mobile layout when `window.innerWidth < 1101`, or after a week whose
 * slots are all shorter than {@link SHORT_SLOT_MAX_MINUTES}. Empty / loading weeks keep the
 * last chosen layout; a week with any longer slot switches back to the timeline.
 */
export function AProductTemplateCalendarView({
	slots = [],
	isSlotsLoading = false,
	onSlotSelect,
	startHour = 7,
	endHour = 20,
	dateFrom,
	dateTo,
	onWeekChange
}: AProductTemplateCalendarViewProps) {
	const [isMobile, setIsMobile] = useState(false)
	const [preferDenseLayout, setPreferDenseLayout] = useState(false)

	useEffect(() => {
		const checkMobile = () => setIsMobile(window.innerWidth < 1101)
		checkMobile()
		window.addEventListener('resize', checkMobile)
		return () => window.removeEventListener('resize', checkMobile)
	}, [])

	useEffect(() => {
		if (isSlotsLoading || slots.length === 0) return
		setPreferDenseLayout(areAllSlotsShorterThan(slots, SHORT_SLOT_MAX_MINUTES))
	}, [isSlotsLoading, slots])

	const useMobileLayout = isMobile || preferDenseLayout

	return (
		<>
			{useMobileLayout ? (
				<AProductTemplateCalendarMobile
					slots={slots}
					isSlotsLoading={isSlotsLoading}
					onSlotSelect={onSlotSelect}
					startHour={startHour}
					endHour={endHour}
					dateFrom={dateFrom}
					dateTo={dateTo}
					onWeekChange={onWeekChange}
					denseSlots={preferDenseLayout}
				/>
			) : (
				<AProductTemplateCalendarDesktop
					slots={slots}
					isSlotsLoading={isSlotsLoading}
					onSlotSelect={onSlotSelect}
					startHour={startHour}
					endHour={endHour}
					dateFrom={dateFrom}
					dateTo={dateTo}
					onWeekChange={onWeekChange}
				/>
			)}
		</>
	)
}
